import { useMemo, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, ShieldAlert, Loader2, AlertCircle, Wallet, FileText, Users, Calculator } from 'lucide-react';
import Layout from '../Layout';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { K2Controls } from '../k2/K2Controls';
import { computeRange, fmtCz, fmtKc } from '../k2/k2utils';
import { useRangeSources } from './useRangeSources';
import { sumDocs, inRange } from './analyticsUtils';
import { KpiStrip } from './KpiStrip';
import { TrendView } from './TrendView';
import { PartnerRanking } from './PartnerRanking';
import { ArticleRanking } from './ArticleRanking';
import { DocsPanel } from './DocsPanel';

const initial = {
  preset: 'this_week',
  custom: { from: format(startOfMonth(new Date()), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  mode: 'compare', view: 'table', book: 'all', warehouse: 'all',
};

function baseTiles(cur, prev, cfg) {
  return [
    { id: 'amount', label: cfg.amountLabel, icon: Wallet, color: '#16A34A', cur: cur.amount, prev: prev.amount, fmt: fmtKc, inverse: cfg.inverse },
    { id: 'count', label: 'Dokladů', icon: FileText, color: '#0EA5E9', cur: cur.count, prev: prev.count, fmt: (v) => v, inverse: cfg.inverse },
    { id: 'avg', label: 'Průměr na doklad', icon: Calculator, color: '#8B5CF6', cur: cur.avg, prev: prev.avg, fmt: fmtKc, inverse: cfg.inverse },
    { id: 'partners', label: cfg.partnerLabel, icon: Users, color: '#F59E0B', cur: cur.partners, prev: prev.partners, fmt: (v) => v },
  ];
}

function useFiltered(cfg, s, partner, docsAll, itemsAll) {
  const warehouses = useMemo(() => (cfg.showWarehouse ? [...new Set(docsAll.map((d) => d.warehouse).filter(Boolean))].sort() : null), [cfg.showWarehouse, docsAll]);
  const warehouse = warehouses?.includes(s.warehouse) ? s.warehouse : 'all';
  const docs = useMemo(() => docsAll.filter((d) => (s.book === 'all' || d.book === s.book)
    && (warehouse === 'all' || d.warehouse === warehouse) && (!partner || d.partner === partner)), [docsAll, s.book, warehouse, partner]);
  const docsByRid = useMemo(() => new Map(docsAll.map((d) => [d.rid, d])), [docsAll]);
  const items = useMemo(() => {
    const ok = new Set(docs.map((d) => d.rid));
    return itemsAll.filter((x) => ok.has(x.doc_rid));
  }, [itemsAll, docs]);
  return { warehouses, warehouse, docs, docsByRid, items };
}

function useBooks(cfg, docsAll, src) {
  const extra = cfg.dynamicBooks ? (cfg.extraSources || []).flatMap((s) => src(s.key).items) : [];
  return useMemo(() => {
    if (!cfg.dynamicBooks) return cfg.books || null;
    const map = new Map();
    [...docsAll, ...extra].forEach((d) => d.book && !map.has(d.book) && map.set(d.book, d.book_name));
    return [{ id: 'all', label: 'Všechny řady' }, ...[...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([id, name]) => ({ id, label: name ? `${id} – ${name}` : `Řada ${id}` }))];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, docsAll, extra.length]);
}

export default function GenericAnalysisPage({ cfg }) {
  const { canViewOverviews } = useAuth();
  const [s, setS] = useState(initial);
  const [partner, setPartner] = useState(null);
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const range = useMemo(() => computeRange(s.preset, s.custom), [s.preset, s.custom]);
  const sources = useMemo(() => [cfg.docsSource, cfg.itemsSource, ...(cfg.extraSources || [])].filter(Boolean), [cfg]);
  const { src, reload, anyLoading } = useRangeSources(sources, range, s.mode);
  const docSrc = src(cfg.docsSource.key);
  const itemSrc = cfg.itemsSource ? src(cfg.itemsSource.key) : null;
  const f = useFiltered(cfg, s, partner, docSrc.items, itemSrc?.items || []);
  const books = useBooks(cfg, docSrc.items, src);
  const cur = sumDocs(f.docs, range.from, range.to);
  const prev = sumDocs(f.docs, range.prevFrom, range.prevTo);
  const compare = s.mode === 'compare';
  const tiles = [...baseTiles(cur, prev, cfg), ...(cfg.extraTiles ? cfg.extraTiles({ cur, prev, src, range }) : [])];
  const Icon = cfg.icon;
  const Extra = cfg.ExtraSection;

  if (!canViewOverviews) {
    return <Layout><div className="py-20 text-center" data-testid="an-no-access"><ShieldAlert className="w-14 h-14 text-[#E2E8F0] mx-auto mb-3" /><p className="text-[#57534E]">Nemáte oprávnění k zobrazení této analýzy.</p></div></Layout>;
  }
  return (
    <Layout>
      <div className="space-y-5" data-testid={`an-page-${cfg.id}`}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/analytika" className="inline-flex items-center gap-1 text-sm text-[#57534E] hover:text-[#16A34A] mb-1" data-testid="an-back-link"><ArrowLeft className="w-4 h-4" /> Analytika</Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-3"><Icon className="w-8 h-8 text-[#16A34A]" /> {cfg.title}</h1>
            <p className="text-[#57534E] mt-1" data-testid="an-range-label">
              Období {fmtCz(range.from)} – {fmtCz(range.to)}{compare && <> · srovnání s {fmtCz(range.prevFrom)} – {fmtCz(range.prevTo)}</>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {docSrc.fetchedAt && <span className="text-xs px-2.5 py-1 rounded-full bg-[#F0FDF4] text-[#166534]" data-testid="an-fetched-at">Data z K2: {new Date(docSrc.fetchedAt).toLocaleString('cs-CZ', { timeZone: 'Europe/Prague' })}</span>}
            <Button variant="outline" size="sm" onClick={reload} disabled={anyLoading} data-testid="an-refresh-btn"><RefreshCw className={`w-4 h-4 mr-2 ${anyLoading ? 'animate-spin' : ''}`} /> Obnovit</Button>
          </div>
        </div>
        <K2Controls s={{ ...s, warehouse: f.warehouse }} set={set} warehouses={f.warehouses} books={books} />
        {docSrc.error && <div className="flex items-center gap-2 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="an-error"><AlertCircle className="w-4 h-4" /> {docSrc.error}</div>}
        {docSrc.loading && !docSrc.fetchedAt ? (
          <div className="py-20 flex justify-center" data-testid="an-loading"><Loader2 className="w-8 h-8 animate-spin text-[#16A34A]" /></div>
        ) : (
          <div className={`space-y-5 transition-opacity ${docSrc.loading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="an-content">
            <KpiStrip tiles={tiles} showCompare={compare} />
            {Extra && <Extra src={src} range={range} book={s.book} />}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-sm" data-testid="an-trend">
              <TrendView docs={f.docs} range={range} mode={s.mode} view={s.view} amountLabel={cfg.amountLabel} />
            </div>
            <PartnerRanking docs={f.docs} range={range} withPrev={compare} label={cfg.partnerLabel} active={partner} onSelect={setPartner} />
            {itemSrc && (
              <ArticleRanking items={f.items} loading={itemSrc.loading} error={itemSrc.error} range={range} withPrev={compare}
                showPrices={cfg.showPrices} docsByRid={f.docsByRid} partnerLabel={cfg.partnerSingular} />
            )}
            <DocsPanel title={cfg.docsTitle} docs={f.docs.filter((d) => inRange(d.date, range.from, range.to))} columns={cfg.docColumns}
              items={itemSrc ? f.items : null} partnerLabel={cfg.partnerSingular} docsByRid={f.docsByRid} />
          </div>
        )}
      </div>
    </Layout>
  );
}
